import React from 'react';
import BasicModal from '../../atoms/BasicModal';
import Dropdown from '@HomaHealth/ui-component-library/lib/_AIQ-components/atoms/Dropdown';
import { Grid } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { Typography } from '@HomaHealth/ui-component-library/lib/_patient-access-components';
import { patientAccessTheme } from '@HomaHealth/ui-component-library/lib/theme/theme';
import {
  FileDropZone,
  UploadedDocument,
} from '@HomaHealth/ui-component-library/lib/_AIQ-components';
import { formatFileSize } from '../../../core-utils/helper-utils/TaskBoardHelper';
import { scrollBarStyles } from '../../../core-utils/messages/CommonConstants';
import { getFileIcon } from '../../../core-utils/helper-utils/financialForensics/reconciliationHelper';

const DEFAULT_ACCEPTED_FILE_TYPES = [
  '.pdf',
  '.doc',
  '.docx',
  '.xls',
  '.xlsx',
  '.csv',
];

export interface DropdownConfig {
  label: string;
  options: string[];
  value: string;
   
  onSelect: (value: string) => void;
  width?: string;
}

export interface DocumentUploadModalProps {
  open: boolean;
  handleClose: () => void;
  handleSubmit: () => void;
  uploadedFiles: File[];
  setUploadedFiles: React.Dispatch<React.SetStateAction<File[]>>;
  title?: string;
  uploadBtnText?: string;
  acceptedFileTypes?: string[];
  dropdownConfig?: DropdownConfig;
}

const useStyles = makeStyles()({
  fileUploadContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: '1rem',
  },
  files: {
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    overflow: 'auto',
    maxHeight: '150px',
    ...scrollBarStyles,
  },
});

const DocumentUploadModal: React.FC<DocumentUploadModalProps> = ({
  open,
  handleClose,
  handleSubmit,
  uploadedFiles,
  setUploadedFiles,
  title = 'Upload Documents',
  uploadBtnText = 'Upload',
  acceptedFileTypes = DEFAULT_ACCEPTED_FILE_TYPES,
  dropdownConfig,
}) => {
  const { classes } = useStyles();

  const isDropdownValid = !dropdownConfig || !!dropdownConfig.value;
  const isSaveBtnDisabled = !isDropdownValid || uploadedFiles.length === 0;

  const handleDropdownChange = (event: any) => {
    dropdownConfig?.onSelect(event.target.value as string);
  };

  const handleDeleteFile = (index: number) => {
    setUploadedFiles((prev) => prev.filter((_, i) => i !== index));
  };

  return (
    <BasicModal
      open={open}
      handleClose={handleClose}
      title={title}
      isSaveButton={true}
      height="auto"
      saveBtnAction={() => {
        handleSubmit();
        handleClose();
      }}
      saveBtnText={uploadBtnText}
      width="600px"
      saveBtnDisabled={isSaveBtnDisabled}
    >
      {/* Dropdown is rendered only when config is provided */}
      {dropdownConfig && (
        <Dropdown
          label={dropdownConfig.label}
          dropDownOptions={dropdownConfig.options}
          dropDownValues={dropdownConfig.options}
          value={dropdownConfig.value}
          handleChange={handleDropdownChange}
          width={dropdownConfig.width ?? '550px'}
          selectStyles={{ width: '540px' }}
        />
      )}

      <Typography
        textColor={patientAccessTheme.palette.text.highemphasis}
        fontSize="14px"
        lineHeight="20px"
        fontType="medium"
        styleProps={{ margin: '1rem 0' }}
      >
        Upload Documents
      </Typography>

      <Grid className={classes.fileUploadContainer}>
        <FileDropZone
          filesSupported={acceptedFileTypes}
          uploadedFiles={uploadedFiles}
          setUploadedFiles={setUploadedFiles}
          isMultiple={true}
          height="auto"
        />
        <Grid
          className={classes.files}
          style={{ paddingRight: uploadedFiles.length > 3 ? '0.5rem' : 0 }}
        >
          {uploadedFiles.map((file: File, index: number) => (
            <UploadedDocument
              key={`${file.name}-${index}`}
              name={file.name}
              size={formatFileSize(file.size)}
              handleDelete={() => handleDeleteFile(index)}
              startIcon={getFileIcon(file.type)}
              width="100%"
              truncateWidth="300px"
            />
          ))}
        </Grid>
      </Grid>
    </BasicModal>
  );
};

export default DocumentUploadModal;
